/****************************************************
 * Module Name:  customerWisePendingPayment.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Constants from "../../utility/constants";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import InvoiceReport from "./invoiceReport";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

const CustomerWisePendingPayment = () => {

    const localPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.REPORT_USERS_CUSTOMERWISEPENDINGPAYMENT);

    const [listData, setListData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [invoiceDetailModal, setInvoiceDetailModal] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [totalPaid, setTotalPaid] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [customerData, setCustomerData] = useState({
        customer: [],
        customerOptions: [],
    });
    const defaultStatus = [{ value: -1, label: "-- ANY --" }]

    useEffect(() => {
        PageTitle("REPORT_USERS_CUSTOMERWISEPENDINGPAYMENT");
        handleSearch();
    }, []);

    const handleSearch = () => {
        setLoader(true)
        let userid = Utility.isReadAllowed(localPermission) ? -1 : Constants.loggedInUser.id;
        ReportProxy.GetAllCustomerWisePendingPayment(userid, Constants.loggedInUser.usertype, (response) => {
            if (response) {
                const transformedData = response.map(item => ({
                    ...item,
                    usertype1: Constants.USER_TYPE[item.usertype],
                }));

                const uniqueFilterValues = {
                    designation: Utility.getDistinctOptions(transformedData, "designation"),
                    usertype: Utility.getDistinctOptions(transformedData, "usertype1"),
                    grade: Utility.getDistinctOptions(transformedData, "grade"),
                    location: Utility.getDistinctOptions(transformedData, "location"),
                    locationcountry: Utility.getDistinctOptions(transformedData, "locationcountry"),
                    city: Utility.getDistinctOptions(transformedData, "city"),
                    state: Utility.getDistinctOptions(transformedData, "state"),
                    country: Utility.getDistinctOptions(transformedData, "country"),
                    title: Utility.getDistinctOptions(transformedData, "title"),
                    companyname: Utility.getDistinctOptions(transformedData, "companyname"),
                }

                setFilterValues(uniqueFilterValues);
                setListData(transformedData.sort((a, b) => b.totalInvoice - a.totalInvoice));
                getSummaryReport(transformedData);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    };

    const getSummaryReport = (response) => {
        let totalAmount = 0, totalPaid = 0, totalPending = 0;
        response.forEach(iData => {
            totalAmount += iData.totalamount;
            totalPaid += iData.paidamount;
            totalPending += iData.pendingamount;
        });

        setTotalAmount(totalAmount);
        setTotalPaid(totalPaid);
        setTotalPending(totalPending);
    }

    const handleClick = (params) => {
        setCustomerData({
            customerOptions: [{ value: params.row.id, label: params.row.companyname }],
            customer: [{ value: params.row.id, label: params.row.companyname }],
        })
        setInvoiceDetailModal(true)
    }

    const columnName = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "username",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            width: 180,
            renderCell: renderCellExpand,
        },
        {
            field: "companyname",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.companyname : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "first_name",
            headerName: "FIRST NAME",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "middle_name",
            headerName: "MIDDLE NAME",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "last_name",
            headerName: "LAST NAME",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
            cellClassName: 'lavenderCell',
        },
        {
            field: "designation",
            headerName: "DESIGNATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.designation : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            disableExport: true
        },
        {
            field: "usertype1",
            headerName: "TYPE",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.usertype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            disableExport: true
        },
        {
            field: "employeeid",
            headerName: "EMPLOYEE ID",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        {
            field: "locationid",
            headerName: "LOCATION ID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.country : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
        },
        {
            field: "gstinno",
            headerName: "GSTIN No",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "panno",
            headerName: "PAN No",
            headerAlign: "center",
            align: "center",
            width: 100,
        },
        {
            field: "totalamount",
            headerName: "TOTAL AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "paidamount",
            headerName: "PAID AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "pendingamount",
            headerName: "PENDING AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "number",
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0.00
        },
        {
            field: "totalInvoice",
            headerName: "TOTAL INVOICE",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => handleClick(params)}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
    ];

    const columns = columnName;

    return (
        <Box>
            {Constants.loggedInUser.usertype !== "B" ?
                < Box >

                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                        handleClose={() => setShowSnackbar(false)}
                    />

                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            marginTop: "16px",
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "84vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: GridToolbar }}
                            rows={listData}
                            columns={columns}
                            loading={loader}
                            onRowDoubleClick={handleClick}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        employeeid: false,
                                        id: false,
                                        middle_name: false,
                                        country: false
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />

                        <Box
                            sx={{
                                position: "absolute",
                                top: 11,
                                right: "293px",
                                width: "59%",
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: "black",
                                fontWeight: 'normal',
                                fontSize: Constants.defaultTableFontSize
                            }}
                        ><p style={{ margin: 0 }}>
                                <span>Total Amount </span> <b>{totalAmount.toFixed(2)}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Paid </span> <b>{totalPaid.toFixed(2)}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Pending </span> <b>{totalPending.toFixed(2)}</b>
                            </p>
                        </Box>

                        <Box
                            style={{
                                position: "absolute",
                                top: 6,
                                right: "262px",
                            }}
                        >
                            {loader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() =>
                                            handleSearch()
                                        }
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {
                        invoiceDetailModal ? (
                            <Modal
                                enforceFocus={false}
                                show={true}
                                onHide={() => setInvoiceDetailModal(false)}
                                backdrop="static"
                                dialogClassName="max-modalSize-Tab"
                            >
                                <Modal.Header closeButton>
                                    <Modal.Title>
                                        <img
                                            src={logo}
                                            height="40pt"
                                            width="40pt"
                                            alt={Constants.COMPANY_SITE}
                                        />
                                        &nbsp;&nbsp;
                                        Invoice Details
                                    </Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <InvoiceReport
                                        loadedCustomers={customerData}
                                        defaultfromdate={null}
                                        defaulttodate={null}
                                        defaultStatus={defaultStatus}
                                        fromOtherComponent={true}
                                        heightFromOtherComponent={"66vh"}
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="contained" color="error" onClick={() => setInvoiceDetailModal(false)}>Close</Button>
                                </Modal.Footer>
                            </Modal>
                        ) : null
                    }

                </Box >
                : <NoPermission />}
        </Box>
    );
};

export default CustomerWisePendingPayment;